import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';

import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import useAsyncEffect from '../common/react-hooks/useAsyncEffect';
import ImageClusterPreview from './ImageClusterPreview';
import { IconButton } from '../common/IconButton';
import ModelContextEditor from '../lego/ModelContextEditor';
import { ImagePreview } from './ImagePreview';
import GridLayoutLeftBar from '../common/layout/GridLayoutLeftBar';
import TextSearchInput from '../common/TextSearchInput';
import BadgeColorHash from '../common/BadgeColorHash';
import SimilarityBadge from '../common/SimilarityBadge';


const pageMax = 50;
export default function ImageClusterFinder({ sampleImage, clusterType, onClusterSelected, onCreateNew, vehicleContext, sortByYear, findSimilarToImages }) {
  const { page } = useContext(LegoAdminPageContext);

  const [context, setContext] = useState(vehicleContext);
  const [clusters, setClusters] = useState(null);
  const [textFilter, setTextFilter] = useState('');
  // When `findSimilarToImages` is given, the average similarity of those images to each loaded
  // cluster, keyed by clusterId (null until fetched). Shown as a SimilarityBadge under each badge.
  const [similarityByClusterId, setSimilarityByClusterId] = useState(null);

  useAsyncEffect(async () => {
    const clustersService = page.service('/services/data/image-clusters');

    let query = { clusterType, 'context.modelId': context.modelId, $limit: pageMax+1 };

    if(context.make) {
      query['context.modelId'] = {$regex: "^"+context.make+'-'}
    }

    if(query['context.modelId'] && context.year) {
      query['images.vehicle.year'] = context.year;
    }

    console.log('CONTEXT', context)

    await page.runAsync(clustersService.find({ query }).then(res => setClusters(res)));
  }, [JSON.stringify([context])]);

  // Once the clusters are loaded, score the given images against every loaded cluster in one call.
  // Keyed on the loaded cluster ids (not the text filter) so filtering the list doesn't refetch.
  useAsyncEffect(async () => {
    setSimilarityByClusterId(null);
    if (!clusters?.length || !findSimilarToImages?.length) return;
    const clusterIds = clusters.map(c => String(c._id));
    try {
      const res = await page.service('/services/data/fusebox-model-embeddings')
        .find({ query: { imageIds: findSimilarToImages, clusterIds } });
      setSimilarityByClusterId(res?.similarities || null);
    } catch (error) {
      // Similarity is a ranking/decision hint, not required for picking a cluster.
      console.warn('Failed to load cluster similarities', error);
    }
  }, [clusters ? clusters.map(c => String(c._id)).join(',') : '', JSON.stringify(findSimilarToImages)]);

  let more = null;

  let clusterList = <div className={'text-center p-3'}>Loading clusters for {context.modelId}...</div>;
  if (clusters) {
    let filteredClusters = clusters;
    if (textFilter) {
      const needle = textFilter.toLowerCase();
      filteredClusters = _.filter(clusters, c => _.includes(JSON.stringify(c).toLowerCase(), needle));
    }

    if (filteredClusters.length) {
      let sortedClusters = _.sortBy(filteredClusters, c => {
        if (sortByYear) {
          let closestYearDiff = _.min(_.map(c.images, img => Math.abs((img.vehicle?.year || 0) - parseInt(sortByYear))));
          console.log('CLOSEST YEAR DIFF ', closestYearDiff)
          return closestYearDiff + 0.5 / c.totalImages;
        }
        else if (similarityByClusterId) {
          return -similarityByClusterId[c._id];
        }
        else {
          return -c.totalImages;
        }
      });

      clusterList = _.map(sortedClusters.slice(0, pageMax), c => {
        const sim = similarityByClusterId?.[c._id];
        return <div key={c._id}>
          <IconButton icon={'west'} onClick={() => onClusterSelected(c)}>
            <BadgeColorHash id={c._id}/><br/>
            {_.isNumber(sim) ? <SimilarityBadge value={sim} title={'Average similarity'}/> : null}
          </IconButton>
          
          <ImageClusterPreview key={c._id} clusterId={c._id} preloadedCluster={c}/>
        </div>;
      });
    } else {
      clusterList = <div className={'text-center p-3'}>
        {textFilter && clusters.length ?
          <>No clusters matching &quot;{textFilter}&quot; (of {clusters.length} loaded)</> :
          <>No clusters found for {context.modelId} 😢</>}
      </div>;
    }

  }

  if(clusters && clusters.length === (pageMax+1)) {
    more = <div className={'alert alert-warning'}>Only showing first {pageMax} clusters</div>;
  } else {

  }


  const createNewBtn = onCreateNew && context.modelId ?
    <div className={'text-center p-2 bg-light-success'}>
      <IconButton icon={'add_circle'} level={'success'} onClick={() => onCreateNew({ modelId: context.modelId })}>
        Create new cluster for {context.modelId}
      </IconButton>
    </div>
    : null;

  return <div>
    <GridLayoutLeftBar className={'bg-light'} style={{height: 'calc(100% - 42px)'}}>
      { sampleImage ?
      <div className={''} style={{ width: '400px' }}>
        <div style={{ width: '400px' }}>
          { sampleImage ? <ImagePreview fluidContain url={sampleImage} zoom/> : <div className={'text-center mt-4'}>No sample image</div> }
        </div>
      </div>
      :
      null }

      <div className={''}>
        <div className={''}>
          <div className={'bg-light p-1'}>
            <div className={'d-flex align-items-center'}>
              <div className={'flex-grow-1'}>
                <ModelContextEditor value={context} onChange={setContext}/>
              </div>
              <div className={'flex-grow-1 ml-2'}>
                <TextSearchInput value={textFilter} onChange={setTextFilter} placeholder={'Filter cluster JSON...'}/>
              </div>
            </div>
            { sortByYear ? <div className={'alert alert-info m-2 p-1'}>Sorting by distance to year <strong>{sortByYear}</strong> and then image count</div> : null }
          </div>
          {createNewBtn}
          {more}
          <div className={'bg-white'}>{clusterList}</div>
        </div>
      </div>
    </GridLayoutLeftBar>
  </div>;
}
